import {
  hideColumn,
  // protectFirstRow
} from "../../../../components/gsheet/format";
import { conditionalFormatRangeColumn, conditionalFormatNumberColumn } from "./format";
import {
  QA_RULES, QAAbnormalNum, QAApobecNum, QAUnusualNum,
  getDRMutations, getOtherMutations, QAAbnormal, QAApobec, QAUnusual } from "../../../../utils/QA";
import { getColumnIndex } from "../../../../components/gsheet";
import deleteGene from './delete-gene';
import {cloneDeep, flatten, property} from 'lodash';

const TABLE_CONFIG = [
  {
    caption: 'Patient alias',
    property: 'ptAlias',
    editable: true, // default uneditable, editable: false
    // hidden: true, // default shown, hidden: false
    // disable: true, // default enabled, disable: false
  },
  {
    caption: 'HIVDB patient ID',
    property: 'existingPatientId',
    editable: true,
    getter: (environment) => {
      const {rawSeq} = environment;
      return rawSeq.existingPatientId;
    }
  },
  {
    caption: 'Accession',
    property: 'accession',
    editable: true,
  },
  {
    caption: 'Isolate Name',
    property: 'isolateName',
    editable: true,
  },
  {
    caption: 'Isolate Date',
    property: 'isolateDate',
    editable: true,
  },
  {
    caption: 'Isolate Date Match',
    property: 'isolateDateMatch',
    editable: true,
    hidden: true,
    getter: (environment) => {
      const {this_header, rawSeq} = environment;
      const dateMatch = rawSeq.isolateDateMatch;
      return this_header.toUI(dateMatch);
    },
    toUI: (value) => {
      if (value === '=') {
        return 'eq';
      } else {
        return 'lteq';
      }
    },
    setter: (environment) => {
      const {rawSeq, newValue, this_header} = environment;
      rawSeq[this_header.property] = this_header.fromUI(newValue);
    },
    fromUI: (newValue) => {
      if (newValue === 'lteq') {
        return '<=';
      } else {
        return '=';
      }
    }
  },
  {
    caption: 'Source',
    property: 'source',
    editable: true,
  },
  {
    caption: 'Species',
    property: 'species',
    editable: true,
  },
  {
    caption: 'Region',
    property: 'region',
    editable: true,
  },
  {
    caption: 'Author\nSubtype',
    property: 'authorSubtype',
    editable: true,
  },
  {
    caption: 'Subtype',
    property: 'subtype',
    editable: false,
  },
  {
    caption: 'Is child',
    property: 'isChild',
    editable: true,
    hidden: true,
  },
  {
    caption: 'Clone Name',
    property: 'cloneName',
    editable: true,
    hidden: true,
    setter: (environment) => {
      const {rawSeq, newValue} = environment;
      rawSeq.cloneName = newValue || '$$AUTO$$';
    }
  },
  {
    caption: 'Clone Method',
    property: 'cloneMethod',
    editable: true,
    hidden: true,
    getter: (environment) => {
      const {rawSeq} = environment;
      if (rawSeq.cloneMethod === 'None') {
        return '';
      } else {
        return rawSeq.cloneMethod;
      }
    },
    setter: (environment) => {
      const {rawSeq, newValue} = environment;
      rawSeq.cloneMethod = newValue || 'None';
    }
  },
  {
    caption: 'Gene',
    property: 'gene',
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return gene.gene;
    }
  },
  {
    caption: 'In HIVDB',
    property: 'existingGene',
    getter: (environment) => {
      const {rawSeq} = environment;
      return rawSeq.existingGenes;
    }
  },
  {
    caption: 'Gene ID',
    property: null,
    hidden: true,
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return gene.uuid;
    }
  },
  {
    caption: 'Alignment ID',
    property: null,
    hidden: true,
    getter: (environment) => {
      const {rawAlign} = environment;
      if (!rawAlign) {
        return '';
      }
      return rawAlign.uuid;
    }
  },
  {
    caption: 'Sequence ID',
    property: null,
    hidden: true,
    getter: (environment) => {
      const {rawSeq} = environment;
      return rawSeq.uuid;
    }
  },
  {
    caption: 'Seq\nMethod',
    property: 'seqMethod',
    editable: true,
    hidden: true,
    getter: (environment) => {
      const {rawSeq, this_header} = environment;
      const seqMethod = rawSeq[this_header.property];
      return this_header.toUI(seqMethod);
    },
    toUI: (value) => {
      if (value === 'Dideoxy') {
        return 'Sanger';
      } else {
        return value;
      }
    },
    setter: (environment) => {
      const {rawSeq, newValue, this_header} = environment;
      rawSeq[this_header.property] = this_header.fromUI(newValue);
    },
    fromUI: (newValue) => {
      if (newValue === 'Sanger') {
        return 'Dideoxy';
      } else {
        return newValue;
      }
    }
  },
  {
    caption: 'Seq\nRange',
    property: 'seqRange',
    formatter: (headers, this_header) => {
      return conditionalFormatRangeColumn(
        headers, this_header.caption, QA_RULES.QA_RULE_COVERAGE
      );
    },
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return `${gene.firstAA} => ${gene.lastAA}`;
    }
  },
  {
    caption: 'Seq\nLength',
    property: 'seqLength',
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return (gene.lastAA - gene.firstAA + 1);
    }
  },
  {
    caption: 'No.\nDRMs',
    property: 'NumDRMs',
    getter: (environment) => {
      const {gene, rawSeq, rawAlign} = environment;

      if (!rawAlign && rawSeq.hivdbDRMs) {
        return rawSeq.hivdbDRMs.length;
      }

      if (!gene) {
        return 0;
      }
      return getDRMutations(gene).map(
        ({shortText}) => {
          return shortText;
        }
      ).length;
    }
  },
  {
    caption: 'No.\nStops Indels Xs',
    property: 'numStopIndelX',
    formatter: (headers, this_header) => {
      return conditionalFormatNumberColumn(
        headers, this_header.caption, QA_RULES.QA_RULE_SEQUENCE_QUALITY
      );
    },
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return QAAbnormalNum(gene);
    }
  },
  {
    caption: 'No.\nApobec',
    property: 'numApobec',
    formatter: (headers, this_header) => {
      return conditionalFormatNumberColumn(
        headers, this_header.caption, QA_RULES.QA_RULE_APOBECS
      );
    },
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return QAApobecNum(gene);
    }
  },
  {
    caption: 'No.\nUnusual',
    property: 'numUnusual',
    formatter: (headers, this_header) => {
      return conditionalFormatNumberColumn(
        headers, this_header.caption, QA_RULES.QA_RULE_UNUSUAL_MUTATIONS
      );
    },
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return QAUnusualNum(gene);
    }
  },
  {
    caption: 'DRMs',
    property: 'drms',
    getter: (environment) => {
      const {gene, rawSeq, rawAlign} = environment;

      if (!rawAlign && rawSeq.hivdbDRMs) {
        return rawSeq.hivdbDRMs;
      }

      if (!gene) {
        return '';
      }
      return getDRMutations(gene).map(
        ({shortText}) => {
          // let comment = [];
          // if (isSDRM) {
          //   comment.push('SDRM');
          // }
          // if (primaryType === 'Major') {
          //   comment.push('Major');
          // }
          // if (comment.length > 0) {
          //   comment = `(${comment.join(',')})`;
          // } else {
          //   comment = '';
          // }
          return shortText;
        }
      ).join(',');
    }
  },
  {
    caption: 'Not SDRMs',
    property: 'notsdrms',
    getter: (environment) => {
      const {gene, rawSeq, rawAlign} = environment;

      if (!rawAlign && rawSeq.hivdbDRMs) {
        return rawSeq.hivdbDRMs;
      }

      if (!gene) {
        return '';
      }
      return getDRMutations(gene).filter(({isSDRM}) => !isSDRM).map(
        ({shortText}) => {
          return shortText;
        }
      ).join(',');
    }
  },
  {
    caption: 'SDRMs',
    property: 'sdrms',
    getter: (environment) => {
      const {gene, rawSeq, rawAlign} = environment;

      if (!rawAlign && rawSeq.hivdbDRMs) {
        return rawSeq.hivdbDRMs;
      }

      if (!gene) {
        return '';
      }
      return getDRMutations(gene).filter(({isSDRM}) => isSDRM).map(
        ({shortText}) => {
          return shortText;
        }
      ).join(',');
    }
  },
  {
    caption: 'Other Muts',
    property: 'othermuts',
    hidden: true,
    getter: (environment) => {
      const {gene, rawSeq, rawAlign} = environment;

      if (!rawAlign && rawSeq.hivdbDRMs) {
        return rawSeq.hivdbDRMs;
      }

      if (!gene) {
        return '';
      }
      return getOtherMutations(gene).map(
        ({shortText}) => {
          return shortText;
        }
      ).join(',');
    }
  },
  {
    caption: 'Stops Indels Xs',
    property: 'stopIndelX',
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return QAAbnormal(gene).map(({shortText}) => shortText).join(',');
    }
  },
  {
    caption: 'Apobec',
    property: 'apobec',
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return QAApobec(gene).map(({shortText}) => shortText).join(',');
    }
  },
  {
    caption: 'Unusual',
    property: 'unusual',
    getter: (environment) => {
      const {gene} = environment;
      if (!gene) {
        return '';
      }
      return QAUnusual(gene).map(({shortText}) => shortText).join(',');
    }
  },
];


class Table {

  constructor() {
    this.table_config = cloneDeep(TABLE_CONFIG);
  }

  get headers() {
    return this.table_config.filter(
      (header) => (!header.disabled)).map(
      (header) => (header.caption));
  }

  getCaption(property_name) {
    let this_header = this.getHeader(property_name);
    return this_header.caption;
  }

  getHeader(property_name) {
    let this_header = null;
    for (const header of this.table_config) {
      if (header.property === property_name) {
        this_header = header;
        break;
      }
    }

    return this_header;
  }

  toUI(property_name, value) {
    let this_header = this.getHeader(property_name);
    if (this_header.toUI) {
      return this_header.toUI(value);
    } else {
      return value;
    }
  }

  enableColumn(property_name) {
    let this_header = this.getHeader(property_name);
    delete this_header.disabled;
  }

  disableColumn(property_name) {
    let this_header = this.getHeader(property_name);
    this_header.disabled = true;
  }

  showColumn(property_name) {
    let this_header = this.getHeader(property_name);
    delete this_header.hidden;
  }

  hideColumn(property_name) {
    let this_header = this.getHeader(property_name);
    this_header.hidden = true;
  }

  get format() {
    let format_rules = [];

    // format_rules.push(protectFirstRow());

    for (const header of this.table_config) {
      if (header.hidden) {
        format_rules.push(
          hideColumn(this.headers, header.caption)
        );
      } else if (header.disabled) {
        continue;
      } else if (header.formatter) {
        format_rules.push(
          header.formatter(this.headers, header)
        );
      }
    }
    return format_rules;
  }

  getter(header, rawSeq, rawAlign) {
    let result = null;
    if (rawAlign) {
      result = rawAlign[header.property];
    }
    if (!result && rawSeq) {
      result = rawSeq[header.property];
    }

    return result;
  }

  getBody(rawDataCollection) {
    const {rawAlignments, rawSequences} = rawDataCollection;
    const {rawSequenceMap} = rawDataCollection;

    let body = [];

    if (rawDataCollection.hasRawAlignments) {

      for (const rawAlign of rawAlignments) {
        const {rawSequenceUuid} = rawAlign;
        const rawSeq = rawSequenceMap[rawSequenceUuid] || null;

        for (const gene of rawAlign.geneSequences) {
          const record = this._getRecord({
            gene: gene,
            rawAlign: rawAlign,
            rawSeq: rawSeq,
          });
          body.push(record);
        }
      }
    } else {
      for (const rawSeq of rawSequences) {
        const record = this._getRecord({
          gene: null,
          rawAlign: null,
          rawSeq: rawSeq,
        });
        body.push(record);
      }
    }

    // body = this.renameIsolateName(body);
    body.unshift(this.headers);

    return body;
  }

  renameIsolateName(record) {
    const ptAliasIdx = this.headers.indexOf('Patient alias');
    const isolateNameIdx = this.headers.indexOf('Isolate Name');
    const isolateDateIdx = this.headers.indexOf('Isolate Date');
    const sourceIdx = this.headers.indexOf('Source');
    const geneIdx = this.headers.indexOf('Gene');

    if (ptAliasIdx < 0 || geneIdx < 0 || isolateNameIdx < 0) {
      return record;
    }
    if (isolateDateIdx < 0 || sourceIdx < 0) {
      return record;
    }

    let result = [];
    let grouped_records = {};

    for (const r of record) {
      const ptAlias = r[ptAliasIdx];
      const isolateDate = r[isolateDateIdx];
      const source = r[sourceIdx];

      if (!ptAlias || !source || !isolateDate) {
        result.push(r);
        continue;
      }

      const key = `${ptAlias}-${isolateDate}-${source}`;
      grouped_records[key] = grouped_records[key] || [];
      grouped_records[key].push(r);
    }

    for (const [key, r_list] of Object.entries(grouped_records)) {
      const grouped_by_isolateName = {};
      for (const r of r_list) {
        const isolateName = r[isolateDateIdx];
        if (!isolateName) {
          result.push(r);
          continue;
        }
        grouped_by_isolateName[isolateName] = grouped_by_isolateName[isolateName] || [];
        grouped_by_isolateName[isolateName].push(r);
      }

      grouped_records[key] = flatten(Object.values(grouped_by_isolateName));
    }

    for (const r_list of Object.values(grouped_records)) {
      let curr_isolate_name = null;
      let gene_counter = {'RT': 0, 'PR': 0, 'IN': 0, 'CA': 0, 'None': 0};
      for (const r of r_list) {
        const isolateName = r[isolateNameIdx];
        const gene = r[geneIdx];

        if (isolateName !== curr_isolate_name) {
          curr_isolate_name = isolateName;
          gene_counter = {'RT': 0, 'PR': 0, 'IN': 0, 'CA': 0, 'None': 0};
        }

        if (!gene) {
          gene_counter['None'] += 1;
          r[isolateNameIdx] = `${curr_isolate_name}_${gene_counter['None']}`;
        } else {
          gene_counter[gene] += 1;
          r[isolateNameIdx] = `${curr_isolate_name}_${gene_counter[gene]}`;
        }
        result.push(r);
      }
    }

    return result;
  }

  _getRecord(environment) {
    const {rawSeq, rawAlign, gene} = environment;
    let record = [];
    for (const header of this.table_config) {
      if (header.disabled) {
        continue;
      } else if (header.getter) {
        record.push(
          header.getter({
            this_header: header,
            gene: gene,
            rawSeq: rawSeq,
            rawAlign: rawAlign
          })
        );
      } else {
        record.push(
          this.getter(header, rawSeq, rawAlign)
        );
      }
    }

    return record;
  }

  validateHeader(new_headers) {
    const {headers} = this;

    return JSON.stringify(headers) === JSON.stringify(new_headers);
  }

  check_if_poorly_sorted(rows, rawDataCollection) {
    const seqUUID2accession = {};
    rawDataCollection.rawSequences.map((s) => {
      seqUUID2accession[s.uuid] = s.accession;
    });

    let sort_error = false;
    for (const row of rows) {
      const rawSeqUUID_index = getColumnIndex(this.headers, 'Sequence ID');
      const rawSeqUUID = row[rawSeqUUID_index];
      const accession_index = getColumnIndex(this.headers, 'Accession');
      const accession = row[accession_index];
      if (accession !== seqUUID2accession[rawSeqUUID]) {
        sort_error = true;
        break;
      }
    }
    if (sort_error) {
      rawDataCollection.addMessage('rawSequences', {
        text: (
          'You have sorted the table without selecting all columns. ' +
          'That caused severe error among the data. ' +
          'Please fresh the page and select all columes to sort table.'
        ),
        level: 'error',
      });
    }
    return !sort_error;
  }

  update(rows, rawDataCollection) {
    let new_headers = rows.shift();
    rawDataCollection.clearMessage();

    if (!this.validateHeader(new_headers)) {
      rawDataCollection.addMessage('rawSequences', {
        text: 'Please do not change table header.',
        level: 'error',
      });
      return rawDataCollection;
    }

    // if (!this.check_if_poorly_sorted(rows, rawDataCollection)) {
    //   return rawDataCollection;
    // }

    deleteGene(rawDataCollection, this.headers, rows);

    const {rawSequenceMap} = rawDataCollection;

    for (const row of rows) {
      const rawSeqUUID_index = getColumnIndex(this.headers, 'Sequence ID');
      const rawSeqUUID = row[rawSeqUUID_index];
      let rawSequence = rawSequenceMap[rawSeqUUID] || null;

      if (!rawSequence) {
        continue;
      }

      for (const header of this.table_config) {
        if (!header.editable || header.disabled) {
          continue;
        }
        const header_index = getColumnIndex(this.headers, header.caption);
        if (header.setter) {
          header.setter({
            rawSeq: rawSequence,
            newValue: row[header_index],
            this_header: header,
          });
        } else {
          rawSequence[header.property] = row[header_index] || null;
        }
      }

    }

    return rawDataCollection;
  }
}

export default new Table();



// WEBPACK FOOTER //
// ./src/views/wizard/isolate/gtable_seq/table-config.js